import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A capped list with a toggle underneath.
 *
 * Every long list on the dashboard grew its own version of this: a `useState`,
 * a `slice`, and a button that only ever opened. Four copies, and all four were
 * one-way — once you had unfolded twenty-one rows the only way back was a page
 * reload. So it is one component now, and it folds shut again.
 *
 * The list is capped rather than scrolled on purpose: a scrollbox hides how much
 * there is and traps the page scroll on a trackpad. A count on the button says
 * exactly what you are about to get.
 */
export function MeerTonen<T>({
  items,
  cap,
  meer,
  minder = 'Minder tonen',
  children,
}: {
  items: readonly T[];
  /** How many to show while collapsed. */
  cap: number;
  /** Label while collapsed, given the number hidden. */
  meer: (rest: number) => string;
  minder?: string;
  children: (zicht: readonly T[]) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rest = items.length - cap;
  const zicht = open || rest <= 0 ? items : items.slice(0, cap);

  return (
    <>
      {children(zicht)}
      {rest > 0 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 12,
            padding: 0,
            background: 'none',
            border: 'none',
            color: '#2A6085',
            fontWeight: 700,
            fontSize: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            aria-hidden
            style={{
              flex: 'none',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .18s ease',
            }}
          >
            <path
              d="M3.5 6L8 10.5L12.5 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {open ? minder : meer(rest)}
        </button>
      )}
    </>
  );
}
